import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_BACKEND_URL, getLiveAccessSessionEmail, getLiveAccessStatus } from '../api/liveAccess';
import { analytics, AnalyticsEvent } from '../services/analytics';

/** Shared email/cookie gate for public /live and /ranking. */
export function useLiveRankingsAccessGate() {
    const queryClient = useQueryClient();

    const [accessUrlFlash] = useState<'invalid' | 'missing' | null>(() => {
        const p = new URLSearchParams(window.location.search).get('live_access');
        if (p === 'invalid' || p === 'missing') return p;
        return null;
    });

    const {
        data: accessStatus,
        isPending: accessStatusPending,
        isError: accessStatusError,
    } = useQuery({
        queryKey: ['live-access-status'],
        queryFn: getLiveAccessStatus,
        staleTime: 30 * 1000,
        retry: 1,
    });

    const canFetchContent =
        !accessStatusPending &&
        (accessStatusError || !accessStatus?.gateEnabled || !!accessStatus?.hasAccess);

    const gatedNoAccess =
        !accessStatusPending &&
        !accessStatusError &&
        Boolean(accessStatus?.gateEnabled) &&
        !accessStatus?.hasAccess;

    const sessionEmailEnabled =
        Boolean(accessStatus?.gateEnabled) &&
        Boolean(accessStatus?.hasAccess) &&
        !accessStatusPending &&
        !accessStatusError;

    const { data: liveAccessSessionEmail } = useQuery({
        queryKey: ['live-access-session-email'],
        queryFn: getLiveAccessSessionEmail,
        enabled: sessionEmailEnabled,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (!liveAccessSessionEmail) return;
        analytics.identify(liveAccessSessionEmail, {
            email: liveAccessSessionEmail,
            live_rankings_verified: true,
        });
        analytics.track(AnalyticsEvent.LIVE_RANKINGS_SESSION, {
            source: 'live_access_cookie',
        });
    }, [liveAccessSessionEmail]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const liveToken = params.get('live_token');
        if (liveToken) {
            window.location.href = `${APP_BACKEND_URL}/api/live/access/verify?token=${encodeURIComponent(liveToken)}`;
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const live = params.get('live_access');
        if (live) {
            if (live === 'ok') {
                void queryClient.invalidateQueries({ queryKey: ['live-access-status'] });
            }
            params.delete('live_access');
            const q = params.toString();
            window.history.replaceState({}, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
        }
    }, [queryClient]);

    return {
        accessStatusPending,
        accessStatusError,
        gatedNoAccess,
        canFetchContent,
        accessUrlFlash,
        gateEnabled: Boolean(accessStatus?.gateEnabled),
    };
}
